import React from "react";

import { UseCases } from "@planzone/core";
import { File } from "@planzone/dto";
import { events } from "@planzone/events";
import {
  useChangePlanzoneLogo,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { useMessage } from "@components/useMessage";
import { FileInput } from "@common/fileInput";
import { ImageCropper } from "@common/imageCropper";
import { Logo as BaseLogo } from "@common/logo";
import { UploadIcon } from "./uploadIcon";
import styles from "./styles.scss";
import { Loader } from "@common/loader";
import { useFilesCache } from "@components/cache";

function Logo({ className }: { className: string }) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const { changePlanzoneLogo, isRequestPending } = useChangePlanzoneLogo();
  const cache = useFilesCache();
  const croppedUri = React.useRef<string | null>(null);
  const [cropSource, setCropSource] = React.useState<{
    uri: string;
    name: string;
  } | null>(null);

  useMessage(
    React.useCallback(
      ({ type, data }) => {
        if (type === events.changePlanzoneLogoSucceeded && croppedUri.current) {
          const { nextLogo } = data;
          fetch(croppedUri.current)
            .then((res) => res.blob())
            .then((blob) => cache.add(nextLogo.uuid, blob));
          croppedUri.current = null;
        }
      },
      [cache],
    ),
  );

  return (
    <Safeguard useCases={[UseCases.changePlanzoneLogo]}>
      {({ canChangePlanzoneLogo }) => (
        <>
          <label className={styles.logo}>
            <BaseLogo className={styles.baseLogo} />
            {canChangePlanzoneLogo && (
              <FileInput
                className={styles.input}
                accepts={[
                  "image/jpeg",
                  "image/png",
                  "image/gif",
                  "image/bmp",
                  "image/webp",
                  "image/svg+xml",
                  "image/tiff",
                  "image/x-icon",
                  "image/heif",
                  "image/heic",
                  "image/vnd.microsoft.icon",
                  "image/x-ms-bmp",
                  "image/avif",
                  "image/jp2",
                  "image/jxr",
                ]}
                input={(file) =>
                  setCropSource({ uri: file.uri, name: file.name })
                }
              />
            )}
            {canChangePlanzoneLogo && (
              <div className={styles.overlay}>
                <UploadIcon className={styles.icon} />
              </div>
            )}
            {isRequestPending && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </label>
          {cropSource && (
            <ImageCropper
              source={cropSource.uri}
              fileName={cropSource.name}
              onConfirm={(croppedFile: File) => {
                if (planzone.data.logo) {
                  cache.remove(planzone.data.logo);
                }
                croppedUri.current = croppedFile.uri;
                setCropSource(null);
                changePlanzoneLogo({ planzone, nextLogo: croppedFile });
              }}
              onCancel={() => setCropSource(null)}
            />
          )}
        </>
      )}
    </Safeguard>
  );
}

export { Logo };
